/**
 * Public "Demo without login" API client.
 *
 * Talks to the two anonymous endpoints under `/api/v1/public/demo`:
 *  - GET  /variations → the curated demo tenants a prospect can jump into
 *  - POST /start      → mints a short-lived, read-only demo JWT
 *
 * Hand-rolled fetch (no orval client) — these are unauthenticated public routes
 * matching the chatbot-widget pattern, and the demo entry must work on the login
 * screen before any session exists.
 */

import { API_BASE_URL } from '../api/config';
import { fetchWithDeadline } from '../api/fetchDeadline';
import type { ApiErrorDetail } from '../utils/errors';
// The two public demo routes DO carry response models, so their payload types
// are generated — imported here rather than re-typed. `DemoVariation` keeps the
// local name because the generated one (route-qualified to distinguish it from
// the demo-provisioning variation, which also has an `available` flag) is
// unreadable at a call site.
import type { ModulesAdminApiPublicDemoRoutesDemoVariationResponse } from '@/generated/api/models/modulesAdminApiPublicDemoRoutesDemoVariationResponse';
import type { StartDemoResponse } from '@/generated/api/models/startDemoResponse';

/** A publicly exposable demo tenant a prospect can jump into (no internal ids). */
export type DemoVariation = ModulesAdminApiPublicDemoRoutesDemoVariationResponse;

export interface DemoVariationsResponse {
  default_variation: string;
  variations: DemoVariation[];
}

export type { StartDemoResponse };

export class DemoApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly errorId: string | null;
  readonly timestamp: string | null;
  /**
   * Axios-compatible shape so the shared `getErrorMessage`/`getErrorDetail`
   * helpers translate this hand-rolled fetch error by code — and surface the
   * server `error_id` + `timestamp` reference — exactly like an axios error.
   * (The demo endpoints are unauthenticated public routes called before any
   * session exists, so they bypass the axios instance entirely.)
   */
  readonly response: { status: number; data: { detail: ApiErrorDetail } };
  constructor(status: number, detail: ApiErrorDetail) {
    super(detail.message);
    this.status = status;
    this.code = detail.code;
    this.errorId = detail.error_id ?? null;
    this.timestamp = detail.timestamp ?? null;
    this.response = { status, data: { detail } };
    this.name = 'DemoApiError';
  }
}

async function parseError(res: Response): Promise<DemoApiError> {
  try {
    const data = await res.json();
    const detail = data?.detail;
    if (detail && typeof detail === 'object') {
      return new DemoApiError(res.status, {
        code: detail.code || 'UNKNOWN_ERROR',
        message: detail.message || `Demo API error ${res.status}`,
        error_id: detail.error_id,
        timestamp: detail.timestamp,
      });
    }
    return new DemoApiError(res.status, {
      code: 'UNKNOWN_ERROR',
      message: typeof detail === 'string' ? detail : `Demo API error ${res.status}`,
    });
  } catch {
    return new DemoApiError(res.status, {
      code: 'UNKNOWN_ERROR',
      message: `Demo API error ${res.status}`,
    });
  }
}

export const demoApi = {
  /**
   * @param reveal Optional key from `?reveal=` that un-hides demo tenants we
   *   don't currently advertise (see demo_access.config.json `hidden`).
   */
  async getVariations(reveal?: string | null): Promise<DemoVariationsResponse> {
    const query = reveal ? `?reveal=${encodeURIComponent(reveal)}` : '';
    const res = await fetchWithDeadline(`${API_BASE_URL}/api/v1/public/demo/variations${query}`, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as DemoVariationsResponse;
  },

  async start(variation?: string): Promise<StartDemoResponse> {
    const res = await fetchWithDeadline(`${API_BASE_URL}/api/v1/public/demo/start`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ variation: variation ?? null }),
    });
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as StartDemoResponse;
  },
};
